import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { numberWithCommas, toFixed } from "src/services/utils";

export default function MarketOverviewOverview() {
  const { pool } = useParams();
  const { fetchMarketOverview } = useAnalysisOverviewSelector((state) => state.fetch);
  const { marketOverviewLoading } = useAnalysisOverviewSelector((state) => state.loading);
  const marketOverview = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.marketOverview, pool);

  useEffect(() => {
    fetchMarketOverview(pool, { number: 720, interval: 1 }, { active: 2, isCheckMetadata: false });
  }, [pool]);

  const { lastSupply, lastBorrow, lastTvl, lastDate } = useMemo(() => {
    if (marketOverview) {
      const date = Highcharts.dateFormat("%b %e, %Y, %k:%M", marketOverview.supply.slice(-1)[0]["x"]);
      return {
        lastSupply: marketOverview.supply.slice(-1)[0]["y"],
        lastBorrow: marketOverview.borrow.slice(-1)[0]["y"],
        lastTvl: marketOverview.tvl.slice(-1)[0]["y"],
        lastDate: date,
      };
    }
    return {
      lastSupply: 0,
      lastBorrow: 0,
      lastTvl: 0,
      lastDate: "",
    };
  }, [marketOverview]);

  const { series } = useMemo(() => {
    if (marketOverview) {
      return {
        series: [
          { name: "Supply", data: [].concat(marketOverview.supply) },
          { name: "Borrow", data: [].concat(marketOverview.borrow) },
          { name: "Total Value Lock", data: [].concat(marketOverview.tvl) },
        ],
      };
    }
    return {
      series: [
        { name: "Supply", data: [] },
        { name: "Borrow", data: [] },
        { name: "Total Value Lock", data: [] },
      ],
    };
  }, [marketOverview]);

  return (
    <OverviewMonitoringWrapper
      isLoading={marketOverviewLoading}
      title="Market Overview"
      overview="The chart shows the Total Supply, Total Borrow, and Total Value Locked of a lending pool."
      to={`/overview-analysis/market-overview/${pool}`}
    >
      <Box width="100%">
        <Box display="flex" flexWrap="wrap">
          <FigureTypography
            value={`$${numberWithCommas(toFixed(lastSupply, 2))}`}
            title="Supply"
            style={{ marginRight: "1rem" }}
          />
          <FigureTypography
            value={`$${numberWithCommas(toFixed(lastBorrow, 2))}`}
            title="Borrow"
            index={1}
            style={{ marginRight: "1rem" }}
          />
          <FigureTypography value={`$${numberWithCommas(toFixed(lastTvl, 2))}`} title="Total Value Lock" index={2} />
        </Box>
        <Typography>{lastDate}</Typography>
      </Box>
      <LineChart
        series={series}
        option={{
          chart: {
            height: "230px",
          },
          ...beautifulTooltip((value) => `$${numberWithCommas(toFixed(value, 2))}`),
          xAxis: {
            visible: false,
          },
        }}
      />
    </OverviewMonitoringWrapper>
  );
}
